const image500 = process.env.REACT_APP_API_IMAGE;
const maxPage = 50;

const checkAdultContent = (actor) => {
  if (actor.known_for) {
    for (let i = 0; i < actor.known_for.length; i++) {
      //there is a lot of adult content not tagged as adult from this coutries
      if (
        ['ko', 'ja', 'th', 'zh', 'in'].includes(
          actor.known_for[i].original_language
        ) ||
        actor.known_for[i].adult === true
      ) {
        return true;
      }
    }
  }
  return false;
};

export const newGame = async () => {
  // Pick two distinct, non-adult actors from a page of results.
  const pickPair = (actorList) => {
    const valid = actorList.filter((actor) => actor && !checkAdultContent(actor));
    if (valid.length < 2) return null;

    const first = valid[Math.floor(Math.random() * valid.length)];
    let second = first;
    // valid.length >= 2 guarantees this terminates.
    while (second.id === first.id) {
      second = valid[Math.floor(Math.random() * valid.length)];
    }
    return [first, second];
  };

  // Try a few random pages until we get a usable pair, so a flaky request or
  // an unlucky page can't leave the game stuck.
  let actorPair = null;
  for (let attempt = 0; attempt < 5 && !actorPair; attempt++) {
    const randomPage = Math.floor(Math.random() * maxPage + 1);
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/person/popular?api_key=${process.env.REACT_APP_API_KEY}&language=en-US&page=${randomPage}`
      );
      const data = await response.json();
      if (data && Array.isArray(data.results)) {
        actorPair = pickPair(data.results);
      }
    } catch (error) {
      console.log('newGame fetch error', error);
    }
  }

  if (!actorPair) {
    throw new Error('Could not load actors for a new game');
  }

  // Add the adult flag and the full image path each actor needs downstream.
  actorPair.forEach((actor) => {
    actor.adultContent = checkAdultContent(actor);
    actor.image = actor.profile_path ? image500 + actor.profile_path : null;
  });

  return actorPair;
};

// Lightweight person lookup (no credits) used to show the target actor's age
// and nationality without the heavy combined_credits payload.
export const getActorInfo = async (id) => {
  try {
    const response = await fetch(
      `https://api.themoviedb.org/3/person/${id}?api_key=${process.env.REACT_APP_API_KEY}&language=en-US`
    );
    const data = await response.json();
    return { ...data, type: 'actor' };
  } catch (error) {
    console.log('getActorInfo error', error);
    return null;
  }
};

export const getActorAPI = async (id) => {
  let data;
  try {
    const response = await fetch(
      `https://api.themoviedb.org/3/person/${id}?api_key=${process.env.REACT_APP_API_KEY}&append_to_response=combined_credits`
    );
    data = await response.json();
  } catch (error) {
    console.log('getActorAPI error', error);
  }

  // PROVIDING THE ACTUAL IMAGE PATH AND A TYPE OF CONTENT
  data.type = 'actor';
  data.image = data.profile_path ? image500 + data.profile_path : null;

  // MODIFYING THE ACTOR'S MOVIES
  data.combined_credits.cast.forEach((production) => {
    if (production.media_type === 'tv') {
      production.release_date = production.first_air_date.substring(0, 4);
      production.title = production.name;
    } else production.release_date = production.release_date.substring(0, 4);

    if (production.poster_path)
      production.image = image500 + production.poster_path;
    else if (production.backdrop_path)
      production.image = image500 + production.backdrop_path;
    else production.image = null;

    production.name = production.title;
    production.type = production.media_type;
  });

  // SORTING THE PRODUCTIONS BY RELEASE DATE
  data.combined_credits.cast.sort((a, b) => {
    return parseInt(a.release_date) >= parseInt(b.release_date) ? -1 : 1;
  });

  // DELETING THE MOVIES THAT HAVE NO RELEASE DATE OR ARE ADULT CONTENT
  data.cast = data.combined_credits.cast.filter((production) => {
    return production.release_date !== '' && production.adult === false;
  });

  // GETTING THE TOP 4 MOVIES
  data.top5 = data.combined_credits.cast
    .sort((a, b) => {
      return a.vote_count >= b.vote_count ? -1 : 1;
    })
    .slice(0, 5);
  // DELETING THE OLD UNFILTERED PRODUCTION LIST
  delete data.combined_credits;

  return data;
};

export const getMovieAPI = async (id) => {
  // GETTING THE DATA FROM THE API
  let data;

  try {
    const response = await fetch(
      `https://api.themoviedb.org/3/movie/${id}?api_key=${process.env.REACT_APP_API_KEY}&append_to_response=credits`
    );
    data = await response.json();
  } catch (error) {
    console.log('Movie API error', error);
  }

  // PROVIDING THE ACTUAL IMAGE PATH AND ADDING THE NAME VALUE TO THE MOVIE
  data.type = 'movie';
  if (data.poster_path) data.image = image500 + data.poster_path;
  else if (data.backdrop_path) data.image = image500 + data.backdrop_path;
  else data.image = null;

  data.name = data.title;

  // MODIFYING THE MOVIES'S ACTORS
  data.credits.cast.forEach((actor) => {
    if (actor.profile_path) actor.image = image500 + actor.profile_path;
    else actor.image = null;

    actor.type = 'actor';
  });

  // Non-adult cast; top5 is the first five, kept in the full list too so the
  // playground can exclude whatever it shows on top without losing anyone.
  const movieCast = data.credits.cast.filter((actor) => actor.adult === false);
  data.top5 = movieCast.slice(0, 5);
  data.cast = movieCast;

  // DELETING THE OLD UNFILTERED CREDITS LIST
  delete data.credits;

  return data;
};

export const getTvAPI = async (id) => {
  // GETTING THE DATA FROM THE API
  const response = await fetch(
    `https://api.themoviedb.org/3/tv/${id}?api_key=${process.env.REACT_APP_API_KEY}&append_to_response=aggregate_credits`
  );
  const data = await response.json();

  // PROVIDING THE ACTUAL IMAGE PATH AND ADDING THE NAME VALUE TO THE MOVIE
  data.type = 'tv';
  if (data.poster_path) data.image = image500 + data.poster_path;
  else if (data.backdrop_path) data.image = image500 + data.backdrop_path;
  else data.image = null;

  // MODIFYING THE MOVIES'S ACTORS
  data.aggregate_credits.cast.forEach((actor) => {
    if (actor.profile_path) actor.image = image500 + actor.profile_path;
    else actor.image = null;

    actor.type = 'actor';
  });

  // Non-adult cast; top5 is the first five, kept in the full list too.
  const tvCast = data.aggregate_credits.cast.filter(
    (actor) => actor.adult === false
  );
  data.top5 = tvCast.slice(0, 5);
  data.cast = tvCast;

  // DELETING THE OLD UNFILTERED CREDITS LIST
  delete data.credits;

  return data;
};
